import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Box, CircularProgress, Divider, Typography } from "@mui/material";
import {
  SIDEBAR_WIDGET_BORDER_COLOR,
  SIDEBAR_WIDGET_CARD_SX,
  SIDEBAR_WIDGET_TITLE_SX,
} from "./sidebarWidgetStyles";

const BORDER = SIDEBAR_WIDGET_BORDER_COLOR;
const TITLE_BLUE = "#3451F4";
const MUTED = "#98A2B3";
const AVATAR_BG = "#F4EBFF";
const AVATAR_TEXT = "#7C3AED";

function getAuthHeaders() {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    "";

  return token
    ? {
        Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
        "x-access-token": token.replace(/^Bearer\s+/i, ""),
      }
    : {};
}

function safeTrim(value) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function getInitials(name = "") {
  const initials = safeTrim(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");

  return initials || "?";
}

function buildPublicBase(apiBase = "") {
  const base = safeTrim(apiBase).replace(/\/$/, "");
  if (!base) return "";
  return base.endsWith("/api") ? base.slice(0, -4) : base;
}

function normalizeAssetUrl(apiBase = "", value = "") {
  const raw = safeTrim(value);
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;

  const publicBase = buildPublicBase(apiBase);
  if (!publicBase) {
    return raw.startsWith("/") ? raw : `/${raw.replace(/^\/+/, "")}`;
  }

  if (raw.startsWith("/")) {
    return `${publicBase}${raw}`;
  }

  return `${publicBase}/${raw.replace(/^\/+/, "")}`;
}

function resolveStatusLabel(row = {}) {
  const candidateStage = safeTrim(row.candidate_stage);
  const candidateStatus = safeTrim(row.candidate_status);
  const stage = safeTrim(row.stage);

  if (candidateStage && !["matched", "sourced"].includes(candidateStage.toLowerCase())) {
    return candidateStage;
  }

  return candidateStatus || candidateStage || stage || "Matched";
}

function getStatusSx(label = "") {
  const normalized = safeTrim(label).toLowerCase();

  if (normalized === "new") {
    return { backgroundColor: "#EAF2FF", color: "#3B82F6" };
  }

  if (["contacted", "matched", "sourced", "screened"].includes(normalized)) {
    return { backgroundColor: "#F3E8FF", color: "#7C3AED" };
  }

  if (["interviewing", "interview", "assessment", "offer"].includes(normalized)) {
    return { backgroundColor: "#FFF4E5", color: "#F97316" };
  }

  if (["available", "open"].includes(normalized)) {
    return { backgroundColor: "#EAFBF1", color: "#22C55E" };
  }

  if (["hired"].includes(normalized)) {
    return { backgroundColor: "#EEF4FF", color: "#3451F4" };
  }

  if (["not available", "rejected", "closed"].includes(normalized)) {
    return { backgroundColor: "#FEECEC", color: "#EF4444" };
  }

  return { backgroundColor: "#F3F4F6", color: "#6B7280" };
}

function dedupeRows(rows = []) {
  const seen = new Set();
  const unique = [];

  rows.forEach((row) => {
    const key =
      safeTrim(row?.candidate_id) ||
      safeTrim(row?.id) ||
      `${safeTrim(row?.candidate_name)}:${safeTrim(row?.candidate_stage)}:${safeTrim(row?.candidate_status)}`;

    if (!key || seen.has(key)) return;
    seen.add(key);
    unique.push(row);
  });

  return unique;
}

function isAiMatchRow(row = {}) {
  return safeTrim(row?.match_source).toLowerCase() !== "manual";
}

export default function JobTopMatchesCard({
  apiBase = "",
  jobId,
  limit = 6,
  onViewAll,
  prefetchedRows = null,
  refreshKey,
  sx = {},
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!Array.isArray(prefetchedRows)) return;
    setRows(prefetchedRows.filter((row) => isAiMatchRow(row)));
    setError("");
  }, [prefetchedRows]);

  useEffect(() => {
    const safeJobId = Number(jobId || 0);
    if (!safeJobId) {
      setRows([]);
      setLoading(false);
      setError("");
      return;
    }

    let active = true;
    const controller = new AbortController();

    setLoading(true);
    setError("");

    fetch(`${apiBase}/api/jobs/${encodeURIComponent(safeJobId)}/matches`, {
      method: "GET",
      headers: {
        ...getAuthHeaders(),
      },
      cache: "no-store",
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const text = await response.text().catch(() => "");
          throw new Error(text || "Failed to load top matches");
        }
        return response.json();
      })
      .then((payload) => {
        if (!active) return;
        setRows(
          (Array.isArray(payload?.rows) ? payload.rows : []).filter((row) =>
            isAiMatchRow(row)
          )
        );
      })
      .catch((err) => {
        if (!active || err?.name === "AbortError") return;
        console.error("Failed to load job top matches:", err);
        setError(err?.message || "Failed to load top matches.");
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBase, jobId, refreshKey]);

  const items = useMemo(() => {
    return dedupeRows(Array.isArray(rows) ? rows : [])
      .sort((left, right) => {
        const leftSort = Number.isFinite(Number(left?.sort_order))
          ? Number(left.sort_order)
          : Number.MAX_SAFE_INTEGER;
        const rightSort = Number.isFinite(Number(right?.sort_order))
          ? Number(right.sort_order)
          : Number.MAX_SAFE_INTEGER;

        if (leftSort !== rightSort) return leftSort - rightSort;

        return Number(right?.match_score || 0) - Number(left?.match_score || 0);
      })
      .slice(0, Math.max(1, Number(limit || 6)))
      .map((row, index) => ({
        id: row?.candidate_id || row?.id || `match-${index}`,
        name: safeTrim(row?.candidate_name) || "Candidate",
        avatarUrl: normalizeAssetUrl(apiBase, row?.avatar_url || ""),
        status: resolveStatusLabel(row),
      }));
  }, [apiBase, limit, rows]);

  return (
    <Box
      className="widget"
      sx={{
        ...SIDEBAR_WIDGET_CARD_SX,
        ...sx,
      }}
    >
      <Box sx={{ px: 4, pt: 4, pb: 1.5 }}>
        <Typography
          sx={{
            ...SIDEBAR_WIDGET_TITLE_SX,
          }}
        >
          Top Matches
        </Typography>

        <Box
          sx={{
            mt: 1.75,
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            alignItems: "center",
            gap: 1.75,
          }}
        >
          <Typography sx={{ fontSize: "12px !important", color: MUTED, fontWeight: 500 }}>
            Name
          </Typography>
          <Typography sx={{ fontSize: "12px !important", color: MUTED, fontWeight: 500 }}>
            Status
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: BORDER }} />

      {loading ? (
        <Box
          sx={{
            px: 4,
            py: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <CircularProgress size={18} sx={{ color: TITLE_BLUE }} />
          <Typography sx={{ fontSize: 12, color: MUTED }}>
            Loading matches...
          </Typography>
        </Box>
      ) : error ? (
        <Box sx={{ px: 4, py: 2.25 }}>
          <Typography sx={{ fontSize: 12, color: "#B42318" }}>{error}</Typography>
        </Box>
      ) : items.length ? (
        <Box sx={{ px: 4 }}>
          {items.map((item, index) => (
            <React.Fragment key={item.id}>
              <Box
                sx={{
                  py: 1.9,
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  alignItems: "center",
                  gap: 1.75,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    minWidth: 0,
                  }}
                >
                  <Avatar
                    src={item.avatarUrl || undefined}
                    sx={{
                      width: 36,
                      height: 36,
                      backgroundColor: AVATAR_BG,
                      color: AVATAR_TEXT,
                      fontSize: 12,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {getInitials(item.name)}
                  </Avatar>

                  <Typography
                    sx={{
                      minWidth: 0,
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#667085",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={item.name}
                  >
                    {item.name}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    px: 1.15,
                    py: 0.6,
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 600,
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                    ...getStatusSx(item.status),
                  }}
                >
                  {item.status}
                </Box>
              </Box>

              {index !== items.length - 1 ? <Divider sx={{ borderColor: BORDER }} /> : null}
            </React.Fragment>
          ))}
        </Box>
      ) : (
        <Box sx={{ px: 4, py: 2.25 }}>
          <Typography sx={{ fontSize: 12, color: MUTED }}>
            No matched candidates yet.
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          px: 4,
          pt: items.length || loading ? 1.35 : 0,
          pb: 2.35,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Typography
          onClick={() => onViewAll?.()}
          sx={{
            fontSize: 12.5,
            fontWeight: 600,
            color: "#7C3AED !important",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          View All
        </Typography>
      </Box>
    </Box>
  );
}
